import styled from "@emotion/styled";
import TipCard from "./TipCard";
import getHints from "../../../../helper/getHints";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { HintsState, nextIsEnabled, prevIsEnabled, updateCurrentHintIndex } from "../../../../features/hints/hintsSlice";
import { NetworkImprovementTipsStatus, PartialServiceState, TipStatus } from "../../../../features/partialService/partialServiceUtils";
import { NQRecord } from "../../../../features/history/types";
import { LangName } from "../../../../types";
import HintJson from "../../../../features/hints/components/HintJson";
import { DeviceFastAsItCanTipId, PartialServiceCallbackTipId, PartialServiceRebootTipId } from "../../../../features/hints/hintsVirtualIds";
import * as R from "ramda";
import { logger } from "../../../../helper/logger";
import { replaceHintVariableByValue } from "../../../../features/hints/hintsVariables";
import fixBodyUrl from "../../../../helper/utils/fixBodyUrl";
import { selectLatestRecord } from "../../../../features/history/selectLatestRecord";
import { fixImageUrl, getCurrentLang } from "../../../../helper/utils";
import NoDataStub from "../../elements/NoDataStub";
import { CommonButtonDiv, RedButtonDiv } from "../../elements/Buttons";
import NavigationButton from "../../elements/NavigationButton";
import { VARIANT } from "../../../../config";
import t from "../../../../helper/t";
import CommonContainer from "../../elements/CommonContainer";

const RootDiv = styled(CommonContainer)(({ theme }) => ([
    {
        display: "flex",
        flexDirection: "column",
        paddingTop: 40,
        paddingBottom: 40,
        paddingLeft: 20,
        paddingRight: 20,

        [theme.splus3.breakpoints.mobile]: {
            paddingTop: 28,
            paddingBottom: 40,
            paddingLeft: 16,
            paddingRight: 16,
        },
    }
]));

const ContainerDiv = styled.div(({ theme }) => ([
    {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gridTemplateRows: "1fr auto",
        columnGap: 20,
        rowGap: 20,

        [theme.splus3.breakpoints.smallDesktop]: {
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr auto auto",
        },

        [theme.splus3.breakpoints.mobile]: {
            gridTemplateColumns: "1fr",
            gridTemplateRows: "1fr auto auto",
            rowGap: 24,
        },
    }
]));

const TipContainerDiv = styled.div(({ theme }) => ([
    {
        gridRow: 1,
        gridColumnStart: 1,
        gridColumnEnd: 3,

        [theme.splus3.breakpoints.mobile]: {
            gridRow: 1,
            gridColumn: 1,
        },
    }
]));

const CompareContainerDiv = styled.div(({ theme }) => ([
    {
        gridRowStart: 1,
        gridRowEnd: 2,
        gridColumn: 3,

        [theme.splus3.breakpoints.smallDesktop]: {
            gridRow: 2,
            gridColumnStart: 1,
            gridColumnEnd: 3,
        },

        [theme.splus3.breakpoints.mobile]: {
            gridRow: 3,
            gridColumn: 1,
        },
    }
]));

const ButtonsContainerDiv = styled.div(({ theme }) => ([
    {
        gridRow: 2,
        gridColumnStart: 1,
        gridColumnEnd: 3,

        display: "flex",
        justifyContent: "flex-end",

        columnGap: 12,

        [theme.splus3.breakpoints.smallDesktop]: {
            gridRow: 3,
        },

        [theme.splus3.breakpoints.mobile]: {
            gridRow: 2,
            gridColumn: 1,
            flexDirection: "column-reverse",
            alignItems: "stretch",
            rowGap: 16,
            marginLeft: "auto",
            marginRight: "auto",
        },
    }
]));

const NextButtonDiv = styled(RedButtonDiv)(({ theme }) => ([
    {
        maxWidth: 286,

        [theme.splus3.breakpoints.mobile]: {
            paddingLeft: 24,
            paddingRight: 24,
        },        
    }
]));

const PrevButtonDiv = styled(CommonButtonDiv)(({ theme }) => ([
    {
        maxWidth: 286,

        [theme.splus3.breakpoints.mobile]: {
            paddingLeft: 20,
            paddingRight: 20,
        },        
    }
]));

interface SpeedtestGuideProps {
}

const SpeedtestGuide: React.FC<SpeedtestGuideProps> = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(s => s);

    const nextTip = () => {
        dispatch(updateCurrentHintIndex(state.hints.currentHintIndex + 1));
    }

    const prevTip = () => {
        dispatch(updateCurrentHintIndex(state.hints.currentHintIndex - 1));
    }

    const hints = getHintsToShow(state.hints, state.partialService, useAppSelector(selectLatestRecord), getCurrentLang());

    return hints.length === 0 ? <NoDataStub /> : (
        <RootDiv>
            <ContainerDiv>
                <TipContainerDiv>
                    <TipCard hints={hints} />
                </TipContainerDiv>

                <CompareContainerDiv>
                    {/* <CompareCard /> */}
                </CompareContainerDiv>

                <ButtonsContainerDiv>
                    {prevIsEnabled(state.hints, hints) &&
                        <NavigationButton onClick={prevTip}>
                            <PrevButtonDiv>
                                {t("Zum vorherigen Tipp")}
                            </PrevButtonDiv>
                        </NavigationButton>
                    }
                    {nextIsEnabled(state.hints, hints) &&
                        <NavigationButton onClick={nextTip}>
                            <NextButtonDiv>
                                {t("Zum nächsten Tipp")}
                            </NextButtonDiv>
                        </NavigationButton>
                    }
                </ButtonsContainerDiv>
            </ContainerDiv>
        </RootDiv>
    );
}

const getHintsToShow = (hintsState: HintsState, partialServiceState: PartialServiceState, lastRecord: NQRecord | undefined, lang: LangName): HintJson[] => {

    const hintIds = getHintIds(hintsState, partialServiceState);
    const allHints = getHints(lang);

    return hintIds
        .map(id => {
            const h = allHints.find(h => h.id === id);

            if (R.isNil(h)) {
                logger.error(`Tip #${id} not found.`);
                return null;
            }

            const teaser = replaceHintVariableByValue(h.teaser, lastRecord);
            if (R.isNil(teaser)) {
                return null;
            }
            const content = R.isNil(h.content) ? h.content : replaceHintVariableByValue(h.content, lastRecord);

            return {
                ...h,
                titleImageUrl: fixImageUrl(h.titleImageUrl)!,
                teaser: fixBodyUrl(teaser),
                teaserImageUrl: fixImageUrl(h.teaserImageUrl),
                content: R.isNil(content) ? undefined : fixBodyUrl(content),
            }
        })
        .filter(h => h !== null)
        .map(h => h!);
}

/* pure */
const getHintIds = (hintsState: HintsState, partialServiceState: PartialServiceState): number[] => {
    const networkImprovementsIds = [10, 11, 16, 17];

    function* getInitialHintId(): Generator<number> {
        if (partialServiceState.callbackTipStatus === TipStatus.Visible) {
            yield PartialServiceCallbackTipId;
        }

        if (partialServiceState.modemResetTipStatus === TipStatus.Visible) {
            yield PartialServiceRebootTipId;
        }

        if (VARIANT === "SPLUS3") {
            yield DeviceFastAsItCanTipId;
        }
    }

    const initialHintIds = Array.from(getInitialHintId());

    return [
        ...initialHintIds,
        ...hintsState.hints
            .map((h, index) => ({ id: h.id, index }))
            .filter(x => {
                if (partialServiceState.networkImprovementTipsStatus === NetworkImprovementTipsStatus.Hidden &&
                    networkImprovementsIds.includes(x.id)) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                if (partialServiceState.networkImprovementTipsStatus === NetworkImprovementTipsStatus.OnFirstPlace) {
                    if (networkImprovementsIds.includes(a.id) && networkImprovementsIds.includes(b.id)) {
                        return a.index - b.index;
                    }
                    if (networkImprovementsIds.includes(a.id)) {
                        return -1;
                    }
                    if (networkImprovementsIds.includes(b.id)) {
                        return 1;
                    }
                }
                return a.index - b.index;
            })
            .map(x => x.id)
    ];
}

export default SpeedtestGuide;
